import { ModalButtonStyles, OpenModal, useTable } from '@composables/common'
import NameAndId from '@src/components/NameAndId.vue'
import UpdateOrCreateSelfDisclosureFormForm from '@src/partials/platforms/UpdateOrCreateSelfDisclosureFormForm.vue'
import { PlatformAttachment, SelfDisclosureForm } from '@src/types'
import { uploadPrivate } from '@src/utils/api/s3'
import { useLanguageStore } from '@src/utils/stores/languages'
import { useUserStore } from '@src/utils/stores/user'
import { computed, h, ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function useSelfDisclosureFormsTable(
    selfDisclosureForms: Ref<SelfDisclosureForm[]>,
    update: (successMessage?: string | null) => Promise<void>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const userStore = useUserStore()
    const i18n = useI18n()
    const languageStore = useLanguageStore()

    const {
        TableWrapper: PlatformSelfDisclosureFormsTable,
        selected: selectedSelfDisclosureFormsIds,
    } = useTable({
        tableProps: {
            id: 'platform_self_disclosure_forms',
            label: 'platform_self_disclosure_forms',
            help: 'help_platform_self_disclosure_forms_table',
            data: selfDisclosureForms,
            total: selfDisclosureForms.value.length,
            columns: [
                {
                    label: 'id',
                    key: 'id',
                    select: true,
                },
                {
                    label: 'id',
                    key: 'id',
                    condition: userStore.can('see_id_columns'),
                    copy: true,
                },
                {
                    label: 'name',
                    key: 'name',
                },
                {
                    label: 'language',
                    key: 'language',
                    cellRenderer: (props: { rowData: PlatformAttachment }) => {
                        return (
                            languageStore.languages.find(
                                (language) =>
                                    language.value === props.rowData.language
                            )?.label || props.rowData.language
                        )
                    },
                },
            ],
            actions: [
                {
                    action: handleViewSelfDisclosureForm,
                    icon: 'fa-eye',
                    title: 'view',
                },
                {
                    action: handleUpdateOrCreateSelfDisclosureForm,
                    icon: 'fa-solid fa-pen',
                    title: 'edit',
                    condition: userStore.can('update_platform'),
                },
                {
                    action: handleDeleteSelfDisclosureForm,
                    icon: 'fas fa-trash-alt',
                    title: 'delete',
                    color: 'text-danger-500',
                    condition: userStore.can('update_platform'),
                },
            ],
        },
    })

    function handleViewSelfDisclosureForm(
        selfDisclosureForm: SelfDisclosureForm
    ) {
        const url = selfDisclosureForm.url
        if (url) {
            window.open(url)
        }
    }

    function handleUpdateOrCreateSelfDisclosureForm(
        selfDisclosureForm: SelfDisclosureForm = new SelfDisclosureForm()
    ) {
        const editedSelfDisclosureForm = ref(new SelfDisclosureForm())
        editedSelfDisclosureForm.value = {
            ...selfDisclosureForm,
        }
        const file = ref<File | null>(null)
        isModalOpen.value = true

        const disableCreate = computed(() => {
            if (editedSelfDisclosureForm.value.id) {
                return !(
                    editedSelfDisclosureForm.value.name &&
                    editedSelfDisclosureForm.value.language
                )
            }
            return !(file.value && editedSelfDisclosureForm.value.name)
        })
        openModal(
            {
                id: 'edit_platform_self_disclosure_form',
                title: editedSelfDisclosureForm.value.id
                    ? 'modal_title_update_platform_self_disclosure_form'
                    : 'modal_title_create_platform_self_disclosure_form',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    saveSelfDisclosureForm(
                        editedSelfDisclosureForm.value,
                        file.value as File
                    ),
            },
            () =>
                h(UpdateOrCreateSelfDisclosureFormForm, {
                    // eslint-disable-next-line quote-props
                    modelValue: editedSelfDisclosureForm,
                    'onUpdate:modelValue': (
                        value: unknown,
                        files: Ref<FileList>
                    ) => {
                        file.value = files.value?.item(0)
                    },
                }),
            {
                submitButtonText: editedSelfDisclosureForm.value.id
                    ? 'update'
                    : 'create',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: disableCreate,
            }
        )
    }

    async function saveSelfDisclosureForm(
        selfDisclosureForm: SelfDisclosureForm,
        file: File
    ) {
        const selfDisclosureFormsTemp = [...selfDisclosureForms.value]
        if (file) {
            const uploadPrivateResponse = await uploadPrivate(file)
            selfDisclosureForm.upload_key = uploadPrivateResponse.data.key
            selfDisclosureForm.url = uploadPrivateResponse.data.temp_storage_url
        }

        if (selfDisclosureForm.id) {
            selfDisclosureForms.value[
                selfDisclosureForms.value.findIndex(
                    (selfDisclosureForm: SelfDisclosureForm) =>
                        selfDisclosureForm.id == selfDisclosureForm.id
                )
            ] = selfDisclosureForm
        } else {
            selfDisclosureForms.value.push(selfDisclosureForm)
        }

        let toast = 'toast_success_platform_self_disclosure_form_updated'
        if (!selfDisclosureForm.id) {
            toast = 'toast_success_platform_self_disclosure_form_created'
        }
        update(
            i18n.t(toast, {
                attachment: selfDisclosureForm.name,
            })
        )
            .catch((error) => {
                selfDisclosureForms.value = selfDisclosureFormsTemp
                throw error
            })
            .then(() => {
                isModalOpen.value = false
            })
    }

    function handleDeleteSelfDisclosureForm(
        selfDisclosureForm: SelfDisclosureForm
    ) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_self_disclosure_form',
                title: 'modal_title_delete_platform_self_disclosure_form',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deleteSelfDisclosureForm(selfDisclosureForm),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: [selfDisclosureForm],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deleteSelfDisclosureForm(
        selfDisclosureFormToDelete: SelfDisclosureForm,
        submit = true
    ) {
        const selfDisclosureFormsTemp = [...selfDisclosureForms.value]
        selfDisclosureForms.value.splice(
            selfDisclosureForms.value.findIndex(
                (selfDisclosureForm) =>
                    selfDisclosureFormToDelete.id == selfDisclosureForm.id
            ),
            1
        )
        if (submit) {
            update(
                i18n.t('toast_success_platform_self_disclosure_form_deleted', {
                    selfDisclosureForm: selfDisclosureFormToDelete.name,
                })
            )
                .catch((error) => {
                    selfDisclosureForms.value = selfDisclosureFormsTemp
                    throw error
                })
                .then(() => {
                    selectedSelfDisclosureFormsIds.value = []
                    isModalOpen.value = false
                })
        }
    }

    function handleDeleteSelfDisclosureForms() {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_self_disclosure_forms',
                title: 'modal_title_delete_platform_self_disclosure_forms',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deleteSelfDisclosureForms(),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: selectedSelfDisclosureForms.value,
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deleteSelfDisclosureForms() {
        const selfDisclosureFormsTemp = [...selfDisclosureForms.value]
        selectedSelfDisclosureForms.value.forEach((selfDisclosureForm) => {
            deleteSelfDisclosureForm(selfDisclosureForm, false)
        })

        update(
            i18n.t('toast_success_platform_self_disclosure_forms_deleted', {
                selfDisclosureForms: selectedSelfDisclosureForms.value
                    .map((selfDisclosureForm) => selfDisclosureForm.name)
                    .join(', '),
            })
        )
            .catch((error) => {
                selfDisclosureForms.value = selfDisclosureFormsTemp
                throw error
            })
            .then(() => {
                selectedSelfDisclosureFormsIds.value = []
                isModalOpen.value = false
            })
    }

    const selectedSelfDisclosureForms = computed(() => {
        return selfDisclosureForms.value.filter(
            (item: SelfDisclosureForm) =>
                item.id &&
                selectedSelfDisclosureFormsIds.value.includes(item.id)
        )
    })

    return {
        PlatformSelfDisclosureFormsTable,
        selectedSelfDisclosureFormsIds,
        handleUpdateOrCreateSelfDisclosureForm,
        handleDeleteSelfDisclosureForms,
    }
}
